/**
 * Fetching a set of photos several at a time, in order, cancellably.
 *
 * Why this exists: iOS will only open the share sheet while the tap that asked
 * for it is still live, about five seconds. Fetching a dozen full-size race
 * photos one after another takes far longer, so the sheet could never be opened
 * from the same tap that started the work, and the athlete had to press twice.
 *
 * The way out is to have the photos already in hand when they tap, which means
 * fetching them in the background beforehand, and fetching them FAST enough to
 * be finished by the time a thumb reaches the button. Sequentially that is
 * hopeless; a handful at a time it is usually done in a couple of seconds.
 *
 * Order is preserved regardless of which request finishes first, because the
 * share sheet shows the files in the order it is given them and an athlete's
 * photos should read in the order the race ran.
 */

export interface PoolOptions<T, R> {
  /** How many to have in flight at once. */
  concurrency?: number;
  /** Called as each one lands, with how many are done so far. */
  onProgress?: (done: number, total: number) => void;
  /** Abort in flight work, for a component that has gone away. */
  signal?: AbortSignal;
  /** Injected in tests. */
  worker: (item: T, index: number) => Promise<R>;
}

export class PoolAbortedError extends Error {
  constructor() {
    super('Aborted');
    this.name = 'PoolAbortedError';
  }
}

/**
 * Run `worker` over every item with a bounded number in flight, resolving to
 * the results IN INPUT ORDER.
 *
 * Rejects on the first failure, because a partial set of photos is not
 * something to hand anybody: the share sheet would silently save some of them
 * and the athlete would have no way to tell which are missing.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  options: PoolOptions<T, R>,
): Promise<R[]> {
  const { concurrency = 4, onProgress, signal, worker } = options;
  const results = new Array<R>(items.length);
  if (items.length === 0) return results;

  let next = 0;
  let done = 0;

  const runOne = async (): Promise<void> => {
    while (true) {
      if (signal?.aborted) throw new PoolAbortedError();
      const index = next++;
      if (index >= items.length) return;
      results[index] = await worker(items[index], index);
      done++;
      onProgress?.(done, items.length);
    }
  };

  const lanes = Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, runOne);
  await Promise.all(lanes);

  if (signal?.aborted) throw new PoolAbortedError();
  return results;
}
